<script setup lang="ts">
/**
 * The notification bell.
 *
 * What it shows is derived server-side from the registers on every request, so this component
 * holds no state worth reconciling: fetch, render, refetch. There is no local list to keep in
 * step with the register, which is the whole reason the feed is derived rather than stored.
 *
 * Polling is deliberately slow. The feed costs a full read of five registers to build, and
 * nothing in it is urgent to the minute — an overdue policy review does not become more overdue
 * between two thirty-second polls. It refreshes on mount, on navigation, and every five minutes
 * while the tab is visible.
 */
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api, type NotificationFeed, type NotificationItem, type NotificationSeverity } from '../lib/api'
import Spinner from './Spinner.vue'

const REFRESH_MS = 5 * 60 * 1000

const route = useRoute()
const router = useRouter()

const feed = ref<NotificationFeed | null>(null)
const loading = ref(false)
const error = ref<string | null>(null)
const open = ref(false)
const busy = ref<string | null>(null)
let timer: number | undefined

async function load() {
  if (loading.value) return
  loading.value = true
  try {
    feed.value = await api.notifications()
    error.value = null
  } catch (err) {
    // A failed fetch must not look like an empty feed. The bell keeps whatever it last had and
    // says the refresh failed, because silently showing zero would be a lie about the backlog.
    error.value = err instanceof Error ? err.message : 'could not load notifications'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void load()
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') void load()
  }, REFRESH_MS)
})
onUnmounted(() => {
  if (timer) window.clearInterval(timer)
})

// Refetch on navigation. Acting on an item almost always means going to a register page, so this
// is when the feed is most likely to have changed.
watch(() => route.fullPath, () => void load())

const unread = computed(() => feed.value?.unread ?? 0)
const items = computed(() => feed.value?.items ?? [])
const mine = computed(() => items.value.filter((i) => i.mine))
const theirs = computed(() => items.value.filter((i) => !i.mine))

/**
 * Tone follows severity, and critical is the only one that gets red. Reserving it matters: a bell
 * that is always red is a bell nobody reads.
 */
const tones: Record<NotificationSeverity, string> = {
  critical: 'border-l-[var(--ax-color-destructive)]',
  high: 'border-l-amber-500',
  medium: 'border-l-[var(--ax-color-primary)]',
  low: 'border-l-[var(--ax-color-border)]',
  info: 'border-l-[var(--ax-color-border)]',
}

function toggle() {
  open.value = !open.value
  if (open.value) void load()
}

async function go(item: NotificationItem) {
  open.value = false
  // Marked read on the way out rather than on open. Opening the panel is not reading the item,
  // and clearing the badge the moment somebody glances at the bell would hide work.
  void markRead([item.key])
  await router.push(item.to)
}

async function markRead(keys: string[]) {
  try {
    await api.markNotificationsRead({ keys })
    await load()
  } catch {
    // Read state is a convenience. Failing to record it changes nothing about the work.
  }
}

async function markAllRead() {
  busy.value = 'all'
  try {
    await api.markNotificationsRead({ all: true })
    await load()
  } finally {
    busy.value = null
  }
}

async function dismiss(item: NotificationItem) {
  busy.value = item.key
  try {
    await api.dismissNotification(item.key)
    await load()
  } finally {
    busy.value = null
  }
}

async function restore() {
  busy.value = 'restore'
  try {
    await api.restoreNotifications()
    await load()
  } finally {
    busy.value = null
  }
}

/**
 * How many items the person has put aside. Only knowable when the feed was not truncated: total
 * counts everything derived, so with a cap in play the difference mixes dismissals with items
 * that simply did not fit, and reporting that as "put aside" would be wrong.
 */
const putAside = computed(() => {
  const f = feed.value
  if (!f || f.truncated) return 0
  return Math.max(0, f.total - f.items.length)
})
</script>

<template>
  <div class="relative">
    <button
      type="button"
      class="relative rounded border border-[var(--ax-color-border)] px-2 py-1 text-xs hover:bg-[var(--ax-color-accent)]"
      :aria-expanded="open"
      :aria-label="unread ? `Notifications, ${unread} unread` : 'Notifications'"
      @click="toggle"
    >
      <svg
        class="size-4"
        viewBox="0 0 20 20"
        fill="none"
        stroke="currentColor"
        stroke-width="1.5"
        aria-hidden="true"
      >
        <path d="M10 3a4 4 0 0 0-4 4v3l-1.5 2.5h11L14 10V7a4 4 0 0 0-4-4Z" stroke-linejoin="round" />
        <path d="M8 15a2 2 0 0 0 4 0" stroke-linecap="round" />
      </svg>
      <span
        v-if="unread > 0"
        class="absolute -right-1.5 -top-1.5 grid min-w-4 place-items-center rounded-full bg-[var(--ax-color-destructive)] px-1 text-[10px] font-semibold leading-4 text-white"
      >
        {{ unread > 99 ? '99+' : unread }}
      </span>
    </button>

    <!-- Click-away. A panel that only closes via its own button traps a phone user. -->
    <div v-if="open" class="fixed inset-0 z-30" aria-hidden="true" @click="open = false"></div>

    <!-- Anchored to the button from the small breakpoint up, and a full-width sheet below it.
         On a phone the bell sits in the middle of the header, so a panel anchored to its right
         edge runs off the left of the screen and clips its own content. -->
    <div
      v-if="open"
      class="fixed inset-x-2 top-14 z-40 max-h-[70vh] overflow-y-auto rounded-lg border border-[var(--ax-color-border)] bg-[var(--ax-color-card)] shadow-lg sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[min(26rem,calc(100vw-2rem))]"
    >
      <div
        class="sticky top-0 flex items-center justify-between gap-2 border-b border-[var(--ax-color-border)] bg-[var(--ax-color-card)] px-3 py-2"
      >
        <span class="text-sm font-semibold">Your compliance work</span>
        <div class="flex items-center gap-2">
          <Spinner v-if="loading" />
          <button
            v-if="unread > 0"
            type="button"
            class="text-xs text-[var(--ax-color-muted-foreground)] hover:underline"
            :disabled="busy === 'all'"
            @click="markAllRead"
          >
            Mark all read
          </button>
        </div>
      </div>

      <p v-if="error" class="border-b border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs">
        Could not refresh: {{ error }}. Showing what was last loaded.
      </p>

      <!-- Notes carry what the server could not read. An empty feed because nothing is wrong and
           an empty feed because a register was unavailable are different facts. -->
      <p
        v-for="note in feed?.notes ?? []"
        :key="note"
        class="border-b border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs"
      >
        {{ note }}
      </p>

      <div v-if="!loading && items.length === 0" class="px-3 py-6 text-center">
        <p class="text-sm">Nothing outstanding.</p>
        <p class="mt-1 text-xs text-[var(--ax-color-muted-foreground)]">
          Reviews, treatment plans and attestations you own would appear here.
        </p>
      </div>

      <template v-for="group in [
        { label: 'Yours to do', list: mine },
        { label: 'Your role answers for these', list: theirs },
      ]">
        <div v-if="group.list.length" :key="group.label">
          <p
            class="bg-[var(--ax-color-muted)]/40 px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[var(--ax-color-muted-foreground)]"
          >
            {{ group.label }}
          </p>
          <div
            v-for="item in group.list"
            :key="item.key"
            class="flex items-start gap-2 border-b border-l-2 border-[var(--ax-color-border)] px-3 py-2"
            :class="[tones[item.severity], item.read ? 'opacity-60' : '']"
          >
            <button type="button" class="min-w-0 flex-1 text-left" @click="go(item)">
              <span class="block text-sm font-medium leading-5">{{ item.title }}</span>
              <span class="mt-0.5 block text-xs leading-4 text-[var(--ax-color-muted-foreground)]">
                {{ item.detail }}
              </span>
              <span
                v-if="item.dueOn"
                class="mono mt-1 block text-[11px] text-[var(--ax-color-muted-foreground)]"
              >
                {{ item.dueOn }}
              </span>
            </button>
            <!-- A 32px target rather than the size of the glyph: this sits next to the row that
                 navigates, and a thumb that misses it goes to the register page instead. -->
            <button
              type="button"
              class="grid size-8 shrink-0 place-items-center rounded text-sm text-[var(--ax-color-muted-foreground)] hover:bg-[var(--ax-color-accent)]"
              :disabled="busy === item.key"
              :aria-label="`Put aside: ${item.title}`"
              title="Put this aside. It comes back if the situation gets worse."
              @click="dismiss(item)"
            >
              ×
            </button>
          </div>
        </div>
      </template>

      <div class="px-3 py-2 text-[11px] text-[var(--ax-color-muted-foreground)]">
        <p v-if="feed?.truncated">
          Showing the worst {{ items.length }} of {{ feed?.total }}. The rest are on the registers.
        </p>
        <p v-else-if="putAside > 0">{{ putAside }} put aside.</p>
        <p>
          Derived from the registers, not stored — nothing here is emailed.
          <button
            type="button"
            class="underline"
            :disabled="busy === 'restore'"
            @click="restore"
          >
            Restore dismissed
          </button>
        </p>
      </div>
    </div>
  </div>
</template>
